import { v4 as uuidv4 } from 'uuid';
import { HandleType } from '@/enums/handle-type.enum';
import { UndoRedoEntry } from '../undoRedo';
import type { PromptData } from '@/types/node';
import type { NodeId, PromptDisplayMode } from 'web';
import type { FlowGraph } from '../FlowGraph';
import type { ClearableAssetsView } from './NodeAssetManager';
import type { AvailableInput } from '@/components/Nodes/PromptNodeComponents/types';

export class PromptView implements ClearableAssetsView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  setPrompt(value: string, ongoing: boolean) {
    const currentValue = this.graph.store.getNodeDataNullable<PromptData>(this.nodeId)?.prompt;
    if (currentValue === value) {
      // To avoid duplicate undo/redo entries
      return;
    }
    this.graph.wasmEdit((wasm) => {
      return wasm.setPrompt(this.nodeId, value);
    }, ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
  }

  clearAllAssets(): UndoRedoEntry {
    return this.graph.wasmChange((wasm) => wasm.setPrompt(this.nodeId, ''));
  }

  hasAssets() {
    return (this.graph.store.getNodeDataNullable<PromptData>(this.nodeId)?.prompt ?? '').length > 0;
  }

  clearOtherAssets(): UndoRedoEntry {
    return UndoRedoEntry.empty();
  }

  hasMultipleAssets(): boolean {
    return false;
  }

  setDisplayMode(mode: PromptDisplayMode) {
    this.graph.wasmEdit((wasm) => {
      return wasm.setPromptDisplayMode(this.nodeId, mode);
    });
  }

  /**
   * Adds a new input handle to the prompt node.
   * Returns the handle key (e.g., "variable1").
   */
  addInputHandle(): AvailableInput {
    const created: AvailableInput = { key: '', label: '' };
    this.graph.edit(() => {
      const result = this.addInputHandleInternal();
      created.key = result.key;
      created.label = result.label;
      return result.undoEntry;
    });
    return created;
  }

  /**
   * Creates a new input handle and appends {{variableN}} to the prompt text.
   * Does NOT wrap in graph.edit() — caller is responsible for that.
   */
  addInputHandleWithVariable(): { key: string; label: string; undoEntry: UndoRedoEntry } {
    const result = this.addInputHandleInternal();

    const promptData = this.graph.store.getNodeDataNullable<PromptData>(this.nodeId);
    const currentPrompt = promptData?.prompt ?? '';
    const separator = currentPrompt.length > 0 && !currentPrompt.endsWith(' ') ? ' ' : '';
    const newPrompt = `${currentPrompt}${separator}{{${result.key}}}`;
    result.undoEntry.add(this.graph.wasmChange((wasm) => wasm.setPrompt(this.nodeId, newPrompt)));

    return result;
  }

  private addInputHandleInternal(): { key: string; label: string; undoEntry: UndoRedoEntry } {
    const { handles } = this.graph.store.getNodeData<PromptData>(this.nodeId);
    const promptCount = Object.keys(handles.input).length;
    const key = `variable${promptCount + 1}`;
    const label = `Variable ${promptCount + 1}`;

    const undoEntry = this.graph.wasmChange((wasm) => wasm.addInputHandle(this.nodeId, key));

    undoEntry.add(
      this.graph.addNodeInputHandle(this.nodeId, key, {
        description: '',
        format: 'text',
        id: uuidv4(),
        order: promptCount,
        required: false,
        label,
        type: HandleType.Text,
      }),
    );

    return { key, label, undoEntry };
  }
}
